// geocoding.js - 카카오맵 지오코딩 유틸리티
import { KAKAO_JAVASCRIPT_KEY } from '../config';

// 카카오 지도 SDK 로더 (지오코딩 전용)
function loadKakaoMapsSdk(apiKey) {
    return new Promise((resolve, reject) => {
        if (window.kakao && window.kakao.maps && window.kakao.maps.services) {
            resolve(window.kakao);
            return;
        }

        const existing = document.querySelector('script[data-kakao-maps-sdk]');
        if (existing) {
            const handleLoaded = () => {
                try {
                    if (!window.kakao) { reject(new Error('Kakao SDK load failed')); return; }
                    const maps = window.kakao.maps;
                    if (typeof maps.load === 'function') {
                        maps.load(() => resolve(window.kakao));
                    } else {
                        resolve(window.kakao);
                    }
                } catch (e) { reject(e); }
            };

            if (existing.getAttribute('data-loaded') === 'true' || existing.readyState === 'complete') {
                handleLoaded();
                return;
            }

            existing.addEventListener('load', () => {
                existing.setAttribute('data-loaded', 'true');
                handleLoaded();
            });
            existing.addEventListener('error', (e) => {
                console.error('[Geocoding] existing script load error', e);
                reject(new Error('Kakao SDK script load failed (existing). 도메인 허용 여부와 앱 키를 확인하세요.'));
            });
            return;
        }

        const script = document.createElement('script');
        script.setAttribute('data-kakao-maps-sdk', 'true');
        script.defer = true;
        script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&autoload=false&libraries=services`;
        script.onload = () => {
            script.setAttribute('data-loaded', 'true');
            if (!window.kakao) { reject(new Error('Kakao SDK load failed')); return; }
            window.kakao.maps.load(() => resolve(window.kakao));
        };
        script.onerror = (e) => {
            console.error('[Geocoding] script error:', e);
            const origin = window.location.origin;
            reject(new Error('Kakao SDK 로드 실패. 등록 도메인 확인: ' + origin));
        };
        document.head.appendChild(script);
    });
}

/**
 * 단일 주소를 좌표로 변환
 * @param {string} address - 변환할 주소
 * @returns {Promise<{lat: number, lng: number}>} 좌표 객체
 */
export async function geocodeAddress(address) {
    if (!address || typeof address !== 'string' || address.trim() === '') {
        throw new Error('유효하지 않은 주소입니다.');
    }

    try {
        const kakao = await loadKakaoMapsSdk(KAKAO_JAVASCRIPT_KEY);
        const geocoder = new kakao.maps.services.Geocoder();

        return new Promise((resolve, reject) => {
            geocoder.addressSearch(address.trim(), (result, status) => {
                if (status === kakao.maps.services.Status.OK) {
                    if (result && result.length > 0) {
                        const coords = result[0];
                        resolve({
                            lat: parseFloat(coords.y),
                            lng: parseFloat(coords.x)
                        });
                    } else {
                        reject(new Error('주소를 찾을 수 없습니다.'));
                    }
                } else {
                    reject(new Error(`지오코딩 실패: ${status}`));
                }
            });
        });
    } catch (error) {
        console.error('지오코딩 오류:', error);
        throw error;
    }
}

/**
 * 고객 객체에 좌표 정보 추가
 * @param {Object} customer - 고객 객체
 * @returns {Promise<Object>} 좌표가 추가된 고객 객체
 */
export async function geocodeCustomer(customer) {
    if (!customer || !customer.address) {
        throw new Error('고객 정보 또는 주소가 없습니다.');
    }

    try {
        const coords = await geocodeAddress(customer.address);
        return {
            ...customer,
            lat: coords.lat,
            lng: coords.lng,
            geocodedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error(`고객 ${customer.name || customer.id} 지오코딩 실패:`, error);
        throw error;
    }
}

/**
 * 여러 고객을 배치로 지오코딩 처리
 * @param {Array} customers - 고객 배열
 * @param {Function} onProgress - 진행률 콜백 (current, total, customer)
 * @param {number} batchSize - 배치 크기 (기본값: 5)
 * @param {number} delayMs - 배치 간 지연 시간 (기본값: 200ms)
 * @returns {Promise<Array>} 지오코딩된 고객 배열
 */
export async function geocodeCustomerBatch(customers, onProgress, batchSize = 5, delayMs = 200) {
    if (!Array.isArray(customers)) {
        throw new Error('고객 배열이 필요합니다.');
    }

    const customersWithAddress = customers.filter(c => c.address && c.address.trim() !== '');
    const customersWithoutCoords = customersWithAddress.filter(c => !c.lat || !c.lng);

    if (customersWithoutCoords.length === 0) {
        return customers;
    }

    const results = [...customers];
    let processed = 0;
    const total = customersWithoutCoords.length;

    // 배치 단위로 처리
    for (let i = 0; i < customersWithoutCoords.length; i += batchSize) {
        const batch = customersWithoutCoords.slice(i, i + batchSize);

        // 배치 내에서 병렬 처리
        // eslint-disable-next-line no-loop-func
        const batchPromises = batch.map(async (customer) => {
            try {
                const geocodedCustomer = await geocodeCustomer(customer);
                processed++;

                // 진행률 콜백 호출
                if (onProgress) {
                    onProgress(processed, total, geocodedCustomer);
                }

                return geocodedCustomer;
            } catch (error) {
                console.warn(`고객 ${customer.name || customer.id} 지오코딩 실패:`, error);
                processed++;

                if (onProgress) {
                    onProgress(processed, total, customer);
                }

                return customer; // 실패한 경우 원본 반환
            }
        });

        await Promise.all(batchPromises);

        // 다음 배치 전 지연 (API 제한 방지)
        if (i + batchSize < customersWithoutCoords.length) {
            await new Promise(resolve => setTimeout(resolve, delayMs));
        }
    }

    return results;
}

/**
 * 고객이 이미 지오코딩되었는지 확인
 * @param {Object} customer - 고객 객체
 * @returns {boolean} 지오코딩 여부
 */
export function isCustomerGeocoded(customer) {
    return !!(customer && customer.lat && customer.lng && customer.geocodedAt);
}

/**
 * 지오코딩이 필요한 고객들 필터링
 * @param {Array} customers - 고객 배열
 * @returns {Array} 지오코딩이 필요한 고객 배열
 */
export function getCustomersNeedingGeocoding(customers) {
    if (!Array.isArray(customers)) return [];

    return customers.filter(customer =>
        customer &&
        customer.address &&
        customer.address.trim() !== '' &&
        !isCustomerGeocoded(customer)
    );
}

/**
 * 지오코딩된 고객들 필터링
 * @param {Array} customers - 고객 배열
 * @returns {Array} 지오코딩된 고객 배열
 */
export function getGeocodedCustomers(customers) {
    if (!Array.isArray(customers)) return [];

    return customers.filter(customer => isCustomerGeocoded(customer));
}
